import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import SupportPeek from "./SupportPeek";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { Pencil, X, Lock, Zap } from "lucide-react";
import { Logo, LogoBare } from "./Logo";
import FeaturesMenu from "./FeaturesMenu";
import EmployersMenu from "./EmployersMenu";
import MobileMenu from "./MobileMenu";
import useCompanyGated from "../hooks/useCompanyGated";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "candidate", label: "Candidate" },
  { value: "company", label: "Company" },
  { value: "test", label: "Test" },
];

function EditCompanyNameModal({ currentName, onClose, onSaved }) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (trimmed === currentName) { onClose(); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: trimmed });
      onSaved(trimmed);
      toast.success("Company name updated");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update company name");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-border w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-semibold text-sm">Edit company name</p>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
          className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all"
          placeholder="Company name"
          maxLength={100}
        />
        <div className="flex gap-2 justify-end mt-4">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm border border-border text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-bold bg-[#0025D8] text-white hover:bg-[#001CB3] transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const { user, setUser, logout, impersonate, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showEditName, setShowEditName] = useState(false);

  const isCompanyAdmin = user?.role === "company" && user?.sub_role !== "employee";
  const { isLocked: companyLocked } = useCompanyGated();

  useEffect(() => {
    if (user?.role !== "candidate") return;
    // Combined AI Apply activity: unread job matches + AI applications applied since last seen.
    api.get("/candidate/ai-apply-badge")
      .then(({ data }) => setUnreadCount(data.total || 0))
      .catch(() => {});
  }, [user]);

  const handleImpersonate = async (targetRole) => {
    if (switching) return;
    setSwitching(true);
    try {
      await impersonate(targetRole);
      navigate(targetRole === "admin" ? "/admin" : "/dashboard");
      toast.success(`Viewing as ${targetRole}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch role");
    } finally {
      setSwitching(false);
    }
  };

  const handleExit = async () => {
    await exitImpersonation();
    navigate("/admin");
    toast.success("Returned to superuser session");
  };

  const showSwitcher = user?.is_superuser;

  const isActiveRole = (roleValue) => {
    if (roleValue === "test") return user?.role === "candidate" && user?.is_test;
    return user?.role === roleValue && !user?.is_test;
  };

  return (
    <>
      <header className="border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <LogoBare className="h-10 w-auto" />
            <span className="hidden sm:inline font-mono text-[10px] text-muted-foreground bracket-label">BETA</span>
          </Link>

          <nav className="flex items-center gap-4">
            {user ? (
              <>
                {/* Superuser role switcher pills */}
                {showSwitcher && (
                  <div className="hidden sm:flex items-center gap-1 border border-border p-1">
                    <span className="font-mono text-[9px] text-muted-foreground px-1 uppercase tracking-wider">View as</span>
                    {ROLES.map((r) => (
                      <button
                        key={r.value}
                        disabled={switching}
                        onClick={() => handleImpersonate(r.value)}
                        className={`font-mono text-[10px] px-2 py-0.5 uppercase tracking-wider transition-colors ${
                          isActiveRole(r.value) && user.impersonating
                            ? "bg-[#F59E0B] text-white"
                            : isActiveRole(r.value) && !user.impersonating
                            ? "bg-[#1D4ED8] text-white"
                            : "text-muted-foreground hover:bg-secondary"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                    {user.impersonating && (
                      <button
                        onClick={handleExit}
                        className="font-mono text-[10px] px-2 py-0.5 uppercase tracking-wider text-[#E11D48] hover:bg-[#E11D48]/10 transition-colors"
                      >
                        Exit
                      </button>
                    )}
                  </div>
                )}

                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-dashboard">{user.role === "admin" ? "Admin" : "Dashboard"}</Link>
                {(user.role === "company" || user.role === "company_employee") && (
                  companyLocked ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground/50 cursor-not-allowed select-none" title="Activate your account to access Roles" data-testid="nav-roles">
                      <Lock className="w-3 h-3" /> Roles
                    </span>
                  ) : (
                    <Link to="/company/roles" className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-roles">Roles</Link>
                  )
                )}
                {user.role === "company" && (
                  companyLocked ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground/50 cursor-not-allowed select-none" title="Activate your account to access Employees" data-testid="nav-employees">
                      <Lock className="w-3 h-3" /> Employees
                    </span>
                  ) : (
                    <Link to="/company/employees" className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-employees">Employees</Link>
                  )
                )}
                {user.role === "company" && (
                  companyLocked ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground/50 cursor-not-allowed select-none" title="Activate your account to access Consent" data-testid="nav-consent">
                      <Lock className="w-3 h-3" /> Consent
                    </span>
                  ) : (
                    <Link to="/company/consent" className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-consent">Consent</Link>
                  )
                )}
                {user.role === "company" && (
                  companyLocked ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground/50 cursor-not-allowed select-none" title="Activate your account to access Integrations" data-testid="nav-integrations">
                      <Lock className="w-3 h-3" /> Integrations
                    </span>
                  ) : (
                    <Link to="/company/integrations" className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-integrations">Integrations</Link>
                  )
                )}
                {(user.role === "company" || user.role === "company_employee") && (
                  companyLocked ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground/50 cursor-not-allowed select-none" title="Activate your account to access Talent Pool" data-testid="nav-talent-pool">
                      <Lock className="w-3 h-3" /> Talent Pool
                    </span>
                  ) : (
                    <Link to="/company/talent-pool" className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-talent-pool">Talent Pool</Link>
                  )
                )}
                {/* Billing: admin-only, reachable even when past_due so they can fix payment. Hidden while pending (no billing yet). */}
                {isCompanyAdmin && user?.company_billing?.company_status !== "pending" && (
                  <Link to="/company/billing" className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-billing">Billing</Link>
                )}
                {user.role === "candidate" && (
                  <Link to="/resumes" className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-resumes">Resumes</Link>
                )}
                {user.role === "candidate" && (
                  <Link to="/apply" className="relative inline-flex items-center gap-1 text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-ai-apply">
                    <Zap className="w-4 h-4 text-[#0025D8]" /> Autopilot
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 bg-[#E11D48] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Link>
                )}
                {user.role === "candidate" && <SupportPeek />}
                {user.role === "candidate" ? (
                  <Link to="/profile" className="hidden sm:flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <div className={`w-8 h-8 rounded-full text-white flex items-center justify-center text-xs font-bold ${user.impersonating ? "bg-[#F59E0B]" : "bg-[#1D4ED8]"}`} data-testid="user-avatar">
                      {user.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="text-xs">
                      <div className="font-semibold">{user.name}</div>
                      <div className="text-muted-foreground uppercase tracking-wider text-[10px]">
                        {user.impersonating ? `${user.role} (impersonating)` : user.role}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="hidden sm:flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full text-white flex items-center justify-center text-xs font-bold ${user.impersonating ? "bg-[#F59E0B]" : "bg-[#1D4ED8]"}`} data-testid="user-avatar">
                      {user.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="text-xs">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold">{user.name}</span>
                        {isCompanyAdmin && !user.impersonating && (
                          <button
                            onClick={() => setShowEditName(true)}
                            className="text-muted-foreground hover:text-[#0025D8] transition-colors"
                            title="Edit company name"
                          >
                            <Pencil className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                      <div className="text-muted-foreground uppercase tracking-wider text-[10px]">
                        {user.impersonating
                          ? `${user.role} (impersonating)`
                          : user.role === "company_employee"
                            ? (user.company_name || "Company")
                            : user.role}
                      </div>
                    </div>
                  </div>
                )}
                <Button
                  variant="outline"
                  className="rounded-none border-border hover:bg-slate-100 hover:text-slate-900"
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="nav-logout"
                >Log out</Button>
              </>
            ) : (
              <>
                {/* Desktop marketing nav */}
                <div className="hidden sm:flex items-center gap-4">
                  <FeaturesMenu />
                  <EmployersMenu />
                  <Link to="/login" className="text-sm font-medium hover:text-[#1D4ED8]" data-testid="nav-login">Log in</Link>
                  <Link to="/signup">
                    <Button className="rounded-none bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="nav-signup">Get started</Button>
                  </Link>
                </div>
                {/* Mobile hamburger */}
                <MobileMenu />
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Impersonation banner */}
      {user?.impersonating && (
        <div className="bg-[#F59E0B] text-white px-6 py-2 flex items-center justify-between gap-4 sticky top-16 z-30">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em]">Superuser</span>
            <span className="font-mono text-[10px] opacity-70">·</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
              Viewing as: <strong>{user.is_test ? "TEST" : user.role.toUpperCase()}</strong>
            </span>
          </div>
          <button
            onClick={handleExit}
            className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold border border-white/40 px-3 py-1 hover:bg-white/20 transition-colors"
          >
            Exit impersonation
          </button>
        </div>
      )}

      {/* Edit company name modal — company admin only */}
      {showEditName && (
        <EditCompanyNameModal
          currentName={user.name}
          onClose={() => setShowEditName(false)}
          onSaved={(newName) => setUser((u) => ({ ...u, name: newName }))}
        />
      )}
    </>
  );
}
